import React, { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';

import { Lock } from "lucide-react";

import { AppConfig } from "../../../config/app.config";

import { AnalysisLoading, AnalysisFooter, ConsentDataGather, PilotContact } from "../../sub/mbts/analysis-v1";
import { AnalysisReportV2 } from "../../sub/mbts/analysis-v2";

import { useGetAnalysis, useGetConversationsForAnalysisReport } from "../../../hooks/mbts-ux";
import { useGetConsentDetails } from "../../../hooks/mbts_consents";

import { CookiesHelper } from "../../../helpers/cookies.helper";
import { AuthHelper } from "../../../helpers/auth.helper";

export const MBTSAnalysisV2 = () => {
    const { scenarioId } = useParams();

    const [isConsentDataGatherVisible, setIsConsentDataGatherVisible] = useState<boolean>(false);
    const [isPilotContactVisible, setIsPilotContactVisible] = useState<boolean>(false);
    const [entitlementError, setEntitlementError] = useState<string | null>(null);

    const { data: report, isLoading, error } = useGetAnalysis(scenarioId ?? null);
    const { data: conversations } = useGetConversationsForAnalysisReport(scenarioId ?? null, report?.analysis_id ?? null);
    const { data: consentDetails } = useGetConsentDetails();

    useEffect(() => {
        if (error) {
            const apiMessage = (error as any)?.response?.data?.message;
            setEntitlementError(apiMessage || error.message || 'You are not entitled to access this scenario.');
        } else {
            setEntitlementError(null);
        };
    }, [error]);

    const isConsentLocked = (): boolean => {
        const isGuestSession = AuthHelper.isGuestTokenValid().result;
        const consentToken = CookiesHelper.get(AppConfig.cookieTokenNames.mbtsConsent);

        return AppConfig.app.isDemo && (isGuestSession && !consentToken);
    };

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight) return;
            if (consentDetails && consentDetails.pilot_contact === null) {
                timeout = setTimeout(() => {
                    setIsPilotContactVisible(true);
                }, 3000);
            };
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (timeout) clearTimeout(timeout);
        };
    }, [consentDetails]);

    return (
        <div className='mbts_analysis__page mbts_analysis__page--v2'>
            {isLoading && !report && (
                <AnalysisLoading />
            )}
            {!isLoading && !!entitlementError && (
                <div className='mbts_analysis__restricted'>
                    <div className='mbts_analysis__restricted__icon'>
                        <Lock />
                    </div>
                    <p className='text-3xl text-bold mb-xs text-center'>Access Restricted</p>
                    <p className='color--txt-tertiary text-center'>{entitlementError}</p>
                </div>
            )}
            {!isLoading && !report && !entitlementError && (
                <p className='text-lg text-semibold color--txt-tertiary text-center'>No analysis found!</p>
            )}
            {!isLoading && !!report && !entitlementError && (
                <div className="mbts_analysis mbts_analysis--v2">
                    <AnalysisReportV2
                        report={report}
                        conversations={conversations ?? []}
                        scenarioId={scenarioId ?? null}
                        isConsentLocked={isConsentLocked()}
                        openConsentDataGather={() => setIsConsentDataGatherVisible(true)}
                    />
                    <div className="mbts_analysis__page--v2__footer_wrap">
                        <AnalysisFooter report={report} />
                    </div>
                    <ConsentDataGather
                        isVisible={isConsentDataGatherVisible}
                        setIsVisible={setIsConsentDataGatherVisible}
                    />
                    <PilotContact
                        isVisible={isPilotContactVisible}
                        setIsVisible={setIsPilotContactVisible}
                    />
                </div>
            )}
        </div>
    );
};
